import React, { useState } from 'react';
import './style.css';

const ChevronDownIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6,9 12,15 18,9"></polyline>
  </svg>
);

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20,6 9,17 4,12"></polyline>
  </svg>
);

const XIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const ClockIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12,6 12,12 16,14"></polyline>
  </svg>
);

const UsersIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const CollapsibleSection = ({
  title,
  items,
  icon,
  type = 'default',
  isExpanded = true,
}) => {
  const contentRef = React.useRef(null);

  const [expanded, setExpanded] = useState(isExpanded);

  if (!items?.length) return null;

  const getItemIcon = () => {
    if (type === 'included') return <CheckIcon />;
    if (type === 'notIncluded') return <XIcon />;
    return null;
  };

  const getSectionClass = () => {
    if (type === 'included') return 'section-included';
    if (type === 'notIncluded') return 'section-not-included';
    if (type === 'competitive') return 'section-competitive';
    return 'section-default';
  };

  return (
    <div className={`package-section ${getSectionClass()}`}>
      <button
        className="section-header"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <div className="section-title">
          {icon && <span className="section-icon">{icon}</span>}
          <h3>{title}</h3>
          <span className="items-count">({items.length})</span>
        </div>
        <div className={`chevron ${expanded ? 'expanded' : ''}`}>
          <ChevronDownIcon />
        </div>
      </button>

      <div
        className="section-content"
        ref={contentRef}
        style={{
          maxHeight: expanded ? contentRef.current?.scrollHeight + 'px' : '0',
          overflow: 'hidden',
          transition: 'max-height 0.3s ease',
        }}
      >
        <ul>
          {items.map((item, idx) => (
            <li key={idx}>
              {getItemIcon() && (
                <span className="item-icon">{getItemIcon()}</span>
              )}
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const InfoCard = ({ title, content, icon }) => (
  <div className="info-card">
    <div className="info-header">
      {icon && <span className="info-icon">{icon}</span>}
      <span className="info-label">{title}</span>
    </div>
    <div className="info-content">{content}</div>
  </div>
);

const PackageDetail = ({ data }) => {
  const {
    name,
    level,
    price,
    description,
    includes,
    features,
    notIncluded,
    clientProvides,
    addOns,
    additionalOptions,
    extraCosts,
    whyCompetitive,
    targetAudience,
    deliveryTime,
  } = data;

  return (
    <div className="package-detail">
      {/* Header Section */}
      <div className="package-header">
        <div className="package-title-section">
          <h1 className="package-title">
            {name}
            <span className="package-level">{level}</span>
          </h1>
          {price && (
            <div className="package-price">
              <span className="price-label">Starting at</span>
              <span className="price-value">{price}</span>
            </div>
          )}
        </div>

        {description && <p className="package-description">{description}</p>}
      </div>

      {/* Quick Info Cards */}
      <div className="quick-info">
        {deliveryTime && (
          <InfoCard
            title="Delivery Time"
            content={deliveryTime}
            icon={<ClockIcon />}
          />
        )}
        {targetAudience?.length > 0 && (
          <InfoCard
            title="Target Audience"
            content={`${targetAudience.length} categories`}
            icon={<UsersIcon />}
          />
        )}
      </div>

      {/* Main Content Sections */}
      <div className="sections-container">
        <CollapsibleSection
          title="What's Included"
          items={includes || features}
          type="included"
          isExpanded={true}
        />

        <CollapsibleSection
          title="Not Included"
          items={notIncluded}
          type="notIncluded"
          isExpanded={false}
        />

        <CollapsibleSection
          title="Client Provides"
          items={clientProvides}
          isExpanded={false}
        />

        <CollapsibleSection
          title="Additional Options"
          items={additionalOptions || addOns}
          isExpanded={false}
        />

        <CollapsibleSection
          title="Extra Costs"
          items={extraCosts}
          isExpanded={false}
        />

        <CollapsibleSection
          title="Why Choose This Package"
          items={whyCompetitive}
          type="competitive"
          isExpanded={false}
        />

        <CollapsibleSection
          title="Perfect For"
          items={targetAudience}
          icon={<UsersIcon />}
          isExpanded={false}
        />
      </div>
    </div>
  );
};

export default PackageDetail;
